import { logError } from '@zg-rentals/log-error';
import { ApiError, ApiRedirectError, isRequestCancellation } from './client/apiClient';
import { fetchCostsAndFees, fetchLocalizedDescription } from './endpoints/listing';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import type { SupportedLocale } from '@/src/i18n/routing';
import { applyV3CostsAndFees } from '@/src/lib/utils/v3ListingDetail';

export async function localizeRentalContent(
  property: PropertyDetailListing,
  locale: SupportedLocale,
  includeCosts = false,
  signal?: AbortSignal,
): Promise<PropertyDetailListing> {
  if (locale !== 'es' || !property.aliasEncoded || (property.active === false && property.isAllowedToRetain === false)) return property;

  const fallback = (error: unknown) => {
    if (isRequestCancellation(error, signal) || error instanceof ApiRedirectError || (error instanceof ApiError && error.status !== undefined)) return null;
    logError({
      error: error instanceof Error ? error : new Error('Rental content translation failed', { cause: error }),
      errorType: 'RentalContentTranslation',
    });
    return null;
  };
  const [description, costs] = await Promise.all([
    fetchLocalizedDescription(property.aliasEncoded, locale, signal).catch(fallback),
    includeCosts && property.active !== false
      ? fetchCostsAndFees(property.aliasEncoded, { locale, signal }).catch(fallback)
      : Promise.resolve(null),
  ]);
  const spanishDescription = description?.contentLocale === 'es' && description.fullDescription?.trim() && property.details
    ? description.fullDescription : undefined;
  const localized = {
    ...property,
    descriptionLocale: spanishDescription ? 'es' : 'en',
    specialOfferTranslations: description?.specialOffers ?? [],
    ...(spanishDescription && property.details
      ? { details: { ...property.details, fullDescription: spanishDescription } }
      : {}),
  } satisfies PropertyDetailListing;
  return costs ? applyV3CostsAndFees(localized, costs) : localized;
}
